import * as React from "react";

import type {
  BulkOrgModelConfigAction,
  BulkOrgModelConfigResponse,
} from "@/shared/domain/orgs";
import { useBulkUpdateOrgModelConfigs } from "@/shared/domain/orgs/hooks";
import { RobotoButton } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";

import styles from "./AiModels.module.css";

interface BulkActionApplyProps {
  orgIds: readonly string[];
  /** The action to apply; `null` until the caller has one (e.g. no model chosen). */
  action: BulkOrgModelConfigAction | null;
  /**
   * Fired with the result once the change has been written. The org
   * searches refetch on their own (the bulk hook invalidates them).
   */
  onApplied: (result: BulkOrgModelConfigResponse) => void;
  /** Rendered before Apply, e.g. a Cancel button. */
  secondaryAction?: React.ReactNode;
}

/**
 * Apply one bulk model-configuration action to the targeted orgs, straight
 * away: no dry run first. The result (who was updated, who skipped and
 * why) is the caller's to show via `onApplied`.
 */
export function BulkActionApply({
  orgIds,
  action,
  onApplied,
  secondaryAction,
}: BulkActionApplyProps) {
  const applyMutation = useBulkUpdateOrgModelConfigs();
  const ready = action !== null && orgIds.length > 0;

  return (
    <div className={styles.applyFlow}>
      {applyMutation.isError && (
        <p className={styles.error} role="alert">
          {userFacingErrorMessage(applyMutation.error)}
        </p>
      )}
      <div className={styles.dialogActions}>
        {secondaryAction}
        <RobotoButton
          disabled={!ready}
          loading={applyMutation.isPending}
          onClick={() => {
            if (ready) {
              applyMutation.mutate(
                { org_ids: [...orgIds], action },
                { onSuccess: onApplied },
              );
            }
          }}
        >
          Apply
        </RobotoButton>
      </div>
    </div>
  );
}

/**
 * Counts, the updated orgs (collapsed), every skipped org with its reason,
 * and the notes on updated orgs (e.g. a removed default that moved).
 */
export function BulkResultSummary({
  result,
  verb,
}: {
  result: BulkOrgModelConfigResponse;
  verb: string;
}) {
  return (
    <div className={styles.result} role="status">
      <p className={styles.resultCounts}>
        {verb} {result.updated.length}, skipped {result.skipped.length}
      </p>
      {result.updated.length > 0 && (
        <details>
          <summary>{verb}</summary>
          <ul className={styles.resultList} aria-label={verb}>
            {result.updated.map((orgId) => (
              <li key={orgId}>{orgId}</li>
            ))}
          </ul>
        </details>
      )}
      {result.skipped.length > 0 && (
        <ul className={styles.resultList} aria-label="Skipped">
          {result.skipped.map(({ org_id, reason }) => (
            <li key={org_id}>
              <span className={styles.orgId}>{org_id}</span>: {reason}
            </li>
          ))}
        </ul>
      )}
      {result.notes !== undefined && result.notes.length > 0 && (
        <ul className={styles.resultList} aria-label="Notes">
          {result.notes.map(({ org_id, note }) => (
            <li key={org_id}>
              <span className={styles.orgId}>{org_id}</span>: {note}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
